import { useMemo } from 'react'

import type { DxTradeOrder } from '@model/DxTrade.types'

import { getOpenLegTargetPrice } from '../utils/formatters'
import { calculatePnL } from '../utils/functions'
import { PortfolioTrade } from './useDxtradePortfolioTrades'

export interface PnLCalculationParams {
  position?: PortfolioTrade
  order?: DxTradeOrder | null
  currentPrice?: number | null
  exitPrice?: number | null
  accountCurrency?: string
  instrumentCurrency?: string | null
  conversionRate?: number | null
}

export function usePnLCalculation(params: PnLCalculationParams): number | null {
  const {
    position,
    order,
    currentPrice,
    exitPrice,
    accountCurrency,
    instrumentCurrency,
    conversionRate,
  } = params

  const side = position?.side ?? order?.side ?? null
  const targetPrice = order ? getOpenLegTargetPrice(order) : undefined
  const openPrice = position?.entryPrice ?? targetPrice ?? null
  const quantity = position?.quantity ?? order?.legs?.[0]?.quantity ?? null

  const requiresConversion = Boolean(
    accountCurrency && instrumentCurrency && accountCurrency !== instrumentCurrency
  )

  return useMemo(() => {
    const effectivePrice = exitPrice ?? currentPrice

    if (side == null || openPrice == null || quantity == null || effectivePrice == null) return null
    if (side !== 'BUY' && side !== 'SELL') return null

    const rawPnl = calculatePnL({ side, openPrice, quantity, exitPrice: effectivePrice })
    if (rawPnl == null) return null

    if (requiresConversion) {
      if (conversionRate == null) return null
      return rawPnl * conversionRate
    }

    return rawPnl
  }, [side, openPrice, quantity, exitPrice, currentPrice, requiresConversion, conversionRate])
}
